import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../../api';
import { upsertAudiosLru } from './audioLru';

export function useAudios(initialPageSize, authorId, bookId, triggerRefresh, storyId) {
  const isEpisode = Boolean(bookId && storyId);
  const [searchTerm, setSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [isLoading, setIsLoading] = useState(false);

  const [audios, setAudios] = useState([]);
  const [audioBaseUrl, setAudioBaseUrl] = useState(null);
  const [pinnedBySource, setPinnedBySource] = useState({ preview: [], modal: [] });

  const [currentPageSize, setCurrentPageSize] = useState(initialPageSize);
  const lastUsedRef = useRef(new Map());
  const ensureInFlightRef = useRef(new Map());
  const audiosRef = useRef(audios);
  const pinsRef = useRef(pinnedBySource);
  audiosRef.current = audios;
  pinsRef.current = pinnedBySource;

  const rememberAudioBaseUrl = useCallback((url) => {
    if (url) {
      setAudioBaseUrl((prev) => prev || url);
    }
  }, []);

  const pinAudioIds = useCallback((source, ids) => {
    const nextIds = (ids || []).filter((id) => id != null).map(String);
    setPinnedBySource((prev) => (
      JSON.stringify(prev[source] || []) === JSON.stringify(nextIds)
        ? prev
        : { ...prev, [source]: nextIds }
    ));
  }, []);

  const currentPinnedIds = () => [
    ...(pinsRef.current.preview || []),
    ...(pinsRef.current.modal || []),
  ];

  const upsertAudio = useCallback((audio) => {
    if (!audio) return;
    setAudios((prev) => upsertAudiosLru(prev, [audio], currentPinnedIds(), lastUsedRef));
  }, []);

  const patchAudio = useCallback((audioId, patch) => {
    setAudios((prev) => prev.map((item) => (
      String(item.id) === String(audioId) ? { ...item, ...patch } : item
    )));
  }, []);

  const ensureAudios = useCallback((ids, source) => {
    const requested = [...new Set((ids || []).filter((id) => id != null).map(String))];
    if (source) {
      pinAudioIds(source, requested);
    }
    if (!isEpisode || requested.length === 0) {
      return;
    }
    const missing = requested.filter(
      (id) => !audiosRef.current.some((audio) => String(audio.id) === id),
    );
    if (missing.length === 0) {
      const now = Date.now();
      requested.forEach((id) => lastUsedRef.current.set(id, now));
      return;
    }
    const requestKey = `${bookId}:${storyId}:${missing.slice().sort().join(',')}`;
    if (ensureInFlightRef.current.has(requestKey)) {
      return;
    }

    const promise = api.get(`/v1/audio/book/${bookId}`, {
      params: {
        storyId,
        ids: missing.join(','),
      },
    })
      .then((res) => {
        if (res.data.audioBaseUrl) {
          setAudioBaseUrl(res.data.audioBaseUrl);
        }
        setAudios((current) => upsertAudiosLru(
          current,
          res.data.audios || [],
          currentPinnedIds(),
          lastUsedRef,
        ));
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error('Error loading step audio:', error);
        }
      })
      .finally(() => {
        ensureInFlightRef.current.delete(requestKey);
      });

    ensureInFlightRef.current.set(requestKey, promise);
  }, [isEpisode, bookId, storyId, pinAudioIds]);

  const refetchAudio = useCallback(
    (cancelToken) => {
      if (isEpisode) {
        const ids = currentPinnedIds();
        if (ids.length > 0) {
          ensureAudios(ids);
        }
        return;
      }

      const params = {
        search: searchTerm || undefined,
        order: 'createdAt:desc',
      };

      if (currentPageSize) {
        params.limit = currentPageSize;
        params.offset = currentPageSize * (currentPage - 1);
      }

      if (authorId) {
        params.authorId = authorId;
      }

      setIsLoading(true);

      api.get('/v1/audio', {
        params,
        cancelToken: cancelToken ? cancelToken.token : undefined,
      })
        .then((res) => {
          const { audios: audiosData } = res.data;
          setTotalCount(Number(res.data.totalCount) || 0);
          setAudios(audiosData);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            console.error('Error loading audio:', error);
          }
        })
        .finally(() => setIsLoading(false));
    },
    [isEpisode, currentPage, currentPageSize, searchTerm, authorId, ensureAudios],
  );

  const updateAudio = useCallback(
    async (audioId, updateData, cancelToken) => {
      const params = {};

      if (bookId) {
        params.bookId = bookId;
      }

      try {
        const response = await api.put(`/v1/audio/${audioId}`, updateData, {
          params,
          cancelToken: cancelToken ? cancelToken.token : undefined,
        });
        return response.data;
      } catch (error) {
        console.error('Error updating audio:', error);
        throw error;
      }
    },
    [bookId],
  );

  useEffect(() => {
    setCurrentPageSize(initialPageSize);
  }, [initialPageSize]);

  useEffect(() => {
    if (isEpisode) {
      const ids = currentPinnedIds();
      if (ids.length > 0) {
        ensureAudios(ids);
      }
      return undefined;
    }
    const cancelToken = axios.CancelToken.source();
    refetchAudio(cancelToken);
    return () => {
      cancelToken.cancel('AudioLibrary unmount');
    };
  }, [refetchAudio, triggerRefresh, isEpisode, ensureAudios]);

  const handleSearchSubmit = (newSearchTerm) => {
    setCurrentPage(1);
    setSearchTerm(newSearchTerm);
  };

  return {
    audios,
    isLoading,
    refetchAudio,
    updateAudio,
    upsertAudio,
    patchAudio,
    ensureAudios,
    pinAudioIds,
    rememberAudioBaseUrl,
    audioBaseUrl,
    bookId,
    storyId,
    currentPage,
    setCurrentPage,
    totalCount,
    handleSearchSubmit,
    searchTerm,
    currentPageSize,
    setCurrentPageSize,
  };
}
